import React, { useState, useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  NodeTypes,
  EdgeTypes,
  Handle,
  Position,
  getBezierPath,
  EdgeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import '../styles/reactflow.css';
import GridNode from '../components/GridNode/GridNode';
import {
  Box,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  EvStation,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { siteConfigService, chargersService } from '../services/api';
import { SiteConfigNode, SiteConfigEdge, SiteConfigGraph, ProjectAsset } from '../types';
import { useAuth } from '../contexts/AuthContext';

// Custom edge component with hover data
const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: EdgeProps) => {
  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Default edge data if not provided (currently unused but kept for potential future use)
  // const edgeData = data || {
  //   power: '50 kW',
  //   current: '100 A',
  //   voltage: '480 V'
  // };

  return (
    <g>
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        //onMouseEnter={() => setIsHovered(true)}
        //onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'pointer' }}
      />
    </g>
  );
};

// Battery SOC component - compact version with box shadows
const BatterySOC = ({ soc = 90 }: { soc?: number }) => {
  const theme = useTheme();
  
  // Determine SOC color based on percentage
  const getSOCColor = (soc: number) => {
    if (soc >= 30) return theme.palette.success.main;
    if (soc >= 15) return theme.palette.warning.light;
    if (soc >= 10) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  // Determine SOC text color based on percentage
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getSOCTextColor = (soc: number) => {
    return soc > 55 ? 'white' : theme.palette.divider;
  };

  const socColor = getSOCColor(soc);
  const socHeight = Math.max(0, Math.min(100, soc)); // Clamp between 0-100

  return (
    <div style={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* Battery terminal - positioned at top */}
      <div style={{
        width: '6px',
        height: '2px',
        backgroundColor: socColor,
        borderRadius: '1px',
        marginBottom: '0px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
      }} />
      
      {/* Battery body */}
      <div style={{
        width: '32px',
        height: '44px',
        backgroundColor: 'white',
        borderRadius: '2px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
      }}>
        {/* SOC-based fill - height proportional to SOC percentage */}
        <div style={{
          position: 'absolute',
          left: '2px',
          right: '2px',
          bottom: '2px',
          height: `${socHeight * 0.4}px`, // 40px available height * percentage
          backgroundColor: socColor,
          borderRadius: '1px',
          transition: 'height 0.3s ease, background-color 0.3s ease'
        }} />
      </div>
    </div>
  );
};

// Custom node types for different site components
const CustomNode = ({ data }: { data: any }) => {
  const theme = useTheme();
  
  const label = data.label || '';
  const labelLower = label.toLowerCase();
  
  // Determine component type based on label prefix
  const isType1 = label.startsWith('Lader Tranby');
  const isType2 = label.startsWith('KE10') || label.startsWith('HB');
  const isType3 = label.startsWith('KE4') || label.startsWith('BP');
  const isType4 = label.startsWith('KE11') || label.startsWith('BC');
  // Zaptec chargers are identified by ZPR prefix (Zaptec Pro) or containing "zaptec" or "pro" in the name
  const isZaptec = label.startsWith('ZPR') || labelLower.includes('zaptec') || (labelLower.includes('pro') && !isType1 && !isType2 && !isType3 && !isType4);
  // Easee chargers use the same single-AC layout as Zaptec. Serials start with EH (Easee Home) or EC (Easee Charge).
  const isEasee = labelLower.startsWith('eh') || labelLower.startsWith('ec') || labelLower.includes('easee');
  const isSingleAcCharger = isZaptec || isEasee;
  const isGrid = label === 'Grid';
  const isSwitchboard = data.assetType === 'switchboard';
  
  // Determine component dimensions
  const getComponentSize = () => {
    if (isSwitchboard) {
      return { width: '80px', height: '160px' };
    }
    if (isType2 || isType4) {
      return { width: '80px', height: '60px' };
    }
    if (isType3) {
      return { width: '80px', height: '60px' }; // Make TYPE3 wider to fit labels inside
    }
    if (isType1 || isSingleAcCharger) {
      return { width: '80px', height: '60px' }; // Make TYPE1, Zaptec, and Easee wider to fit label inside
    }
    return { width: '40px', height: '40px' };
  };
  
  const componentSize = getComponentSize();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
    }}>
      {/* Component label */}
      <div style={{
        textAlign: 'center',
        fontSize: '8px',
        lineHeight: '12px',
        color: theme.palette.divider,
        fontWeight: 500,
        maxWidth: '80px',
        wordWrap: 'break-word',
      }}>
        {label}
      </div>
      
      {/* Main component */}
      <div style={{
        display: 'inline-flex',
        width: componentSize.width,
        height: componentSize.height,
        padding: '8px',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderRadius: '8px',
        position: 'relative',
        backgroundColor: 'white',
      }}>
        {/* Input handle on the left */}
        <Handle
          type="target"
          position={Position.Left}
          style={{
            background: theme.palette.success.main,
            width: '8px',
            height: '8px',
            ...((isType2 || isType4 || isSwitchboard) && { top: '50%' })
          }}
        />
        
        {/* TYPE2 and TYPE4: Show battery indicator */}
        {(isType2 || isType4) && (
          <div style={{ 
            position: 'absolute',
            left: '37.5%',
            top: '45%',
            transform: 'translate(-50%, -50%)'
          }}>
             <BatterySOC soc={100} />
          </div>
        )}
        
        {/* TYPE1, TYPE3, Zaptec, and Easee: Show icon if needed */}
        {(isType1 || isType3 || isSingleAcCharger) && (
          <img
            src={process.env.PUBLIC_URL + '/Chargers.svg'}
            alt={label}
            style={{
              width: '24px',
              height: '24px',
              ...((isType1 || isType3 || isSingleAcCharger) && { position: 'absolute', left: '14px' }) // Position icon on left for TYPE1, TYPE3, Zaptec, and Easee to make room for labels
            }}
          />
        )}
        
        {/* TYPE1: Lader Tranby: One AC outlet with label inside */}
        {isType1 && (
          <>
            {/* Vertical line dividing the box between icon and label */}
            <div style={{
              position: 'absolute',
              right: '28px',
              top: '0px',
              bottom: '0px',
              width: '1px',
              backgroundColor: '#CDC9C5',
            }} />
            
            <Handle
              id="ac-outlet"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '50%',
              }}
            />
            <div style={{
              position: 'absolute',
              right: '2px', // Inside the box, near the handle
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              AC
            </div>
          </>
        )}
        
        {/* Zaptec and Easee: One AC outlet with label inside (similar to TYPE1) */}
        {isSingleAcCharger && (
          <>
            {/* Vertical line dividing the box between icon and label */}
            <div style={{
              position: 'absolute',
              right: '28px',
              top: '0px',
              bottom: '0px',
              width: '1px',
              backgroundColor: '#CDC9C5',
            }} />
            
            <Handle
              id="ac-outlet"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '50%',
              }}
            />
            <div style={{
              position: 'absolute',
              right: '2px', // Inside the box, near the handle
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              AC
            </div>
          </>
        )}
        
        {/* TYPE2: KE10 og HB: AC outlet and CCS1 outlet, with battery */}
        {isType2 && (
          <>
            {/* Vertical line dividing the battery box */}
            <div style={{
              position: 'absolute',
              right: '28px',
              top: '0px',
              bottom: '0px',
              width: '1px',
              backgroundColor: '#CDC9C5',
            }} />
            
            {/* AC outlet on right */}
            <Handle
              id="ac-outlet"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '25%',
              }}
            />
            <div style={{
              position: 'absolute',
              right: '2px',
              top: '25%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              AC
            </div>
            
            {/* CCS1 outlet on right */}
            <Handle
              id="CCS1"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '75%',
              }}
            />
            <div style={{
              position: 'absolute',
              right: '2px',
              top: '75%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              CCS1
            </div>
          </>
        )}
        
        {/* TYPE4: KE11 og BC: AC outlet, CCS1 outlet, and CCS2 outlet, with battery */}
        {isType4 && (
          <>
            {/* Vertical line dividing the battery box */}
            <div style={{
              position: 'absolute',
              right: '28px',
              top: '0px',
              bottom: '0px',
              width: '1px',
              backgroundColor: '#CDC9C5',
            }} />
            
            {/* AC outlet on right */}
            <Handle
              id="ac-outlet"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '20%',
              }}
            />
            <div style={{
              position: 'absolute',
              right: '4px',
              top: '20%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              AC
            </div>
            
            {/* CCS1 outlet on right */}
            <Handle
              id="CCS1"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '50%',
              }}
            />
            <div style={{
              position: 'absolute',
              right: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              CCS1
            </div>
            
            {/* CCS2 outlet on right */}
            <Handle
              id="CCS2"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '80%',
              }}
            />
            <div style={{
              position: 'absolute',
              right: '4px',
              top: '80%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              CCS2
            </div>
          </>
        )}
        
        {/* Switchboard: up to 11 numbered outlets */}
        {isSwitchboard && (
          <>
            <img
              src={process.env.PUBLIC_URL + '/Switchboards.svg'}
              alt={label}
              style={{
                width: '24px',
                height: '24px',
              }}
            />
            {Array.from({ length: 11 }, (_, index) => (
              <React.Fragment key={`outlet-${index + 1}`}>
                <Handle
                  id={`outlet-${index + 1}`}
                  type="source"
                  position={Position.Right}
                  style={{
                    background: theme.palette.success.main,
                    width: '6px',
                    height: '6px',
                    top: `${(index + 1) * 8}%`,
                  }}
                />
                <div style={{
                  position: 'absolute',
                  right: '10px',
                  top: `${(index + 1) * 8}%`,
                  transform: 'translateY(-50%)',
                  fontSize: '8px',
                  color: '#000000',
                  fontWeight: 400,
                }}>
                  {index + 1}
                </div>
              </React.Fragment>
            ))}
          </>
        )}

        {/* TYPE3: KE4 og BP: CCS1 and CCS2 outlets with labels inside */}
        {isType3 && (
          <>
            {/* Vertical line dividing the box between icon and labels */}
            <div style={{
              position: 'absolute',
              right: '28px',
              top: '0px',
              bottom: '0px',
              width: '1px',
              backgroundColor: '#CDC9C5',
            }} />
            
            <Handle
              id="ccs1"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '25%',
              }}
            />
            <div style={{
              position: 'absolute',
              right: '4px', // Inside the box, near the handle
              top: '25%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              CCS1
            </div>
            <Handle
              id="ccs2"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '75%',
              }}
            />
            <div style={{
              position: 'absolute',
              right: '4px', // Inside the box, near the handle
              top: '75%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              CCS2
            </div>
          </>
        )}
        
        {/* Other: Only one inlet (no output handles) */}
        {!isType1 && !isType2 && !isType3 && !isType4 && !isSingleAcCharger && !isGrid && !isSwitchboard && (
          // No output handles for other types
          null
        )}
      </div>
    </div>
  );
};

const nodeTypes: NodeTypes = {
  custom: CustomNode,
  grid: GridNode,
};

const edgeTypes: EdgeTypes = {
  custom: CustomEdge,
};

const initialNodes: Node[] = [];

const initialEdges: Edge[] = [];

const SiteConfig: React.FC = () => {
  const theme = useTheme();
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Site config API state
  const [siteConfigLoading, setSiteConfigLoading] = useState(false);
  const [siteConfigError, setSiteConfigError] = useState<string | null>(null);
  const [apiDataLoaded, setApiDataLoaded] = useState(false);

  // Assets API state
  const [assets, setAssets] = useState<ProjectAsset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState<string | null>(null);
  const [usedAssetIds, setUsedAssetIds] = useState<Set<string>>(new Set());

  // Function to fetch site config data from API
  const fetchSiteConfigData = async () => {
    if (!projectId || !user?.orgId) {
      return;
    }

    setSiteConfigLoading(true);
    setSiteConfigError(null);

    try {
      console.log('Fetching site config data for project:', projectId, 'org:', user.orgId);
      const response = await siteConfigService.getSiteConfigProject(projectId, user.orgId);
      console.log('Site config API response:', response);
      
      if (response.success && response.data) {
        console.log('Site config data received:', response.data);
        
        // Check if graph data exists and has nodes/edges
        if (response.data.graph && response.data.graph.nodes && response.data.graph.edges) {
          // Convert API response to React Flow format
          const flowNodes: Node[] = response.data.graph.nodes.map((node: SiteConfigNode) => ({
            id: node.id,
            type: node.data.label === 'Grid' ? 'grid' : 'custom',
            position: node.position,
            data: {
              label: node.data.label,
              ...(node.data.assetType && { assetType: node.data.assetType }),
            },
          }));

          const flowEdges: Edge[] = response.data.graph.edges.map((edge: SiteConfigEdge) => {
            console.log('Mapping edge:', edge);
            const flowEdge = {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              type: 'default',
              ...(edge.sourceHandle && { sourceHandle: edge.sourceHandle }),
            };
            console.log('Created flow edge:', flowEdge);
            return flowEdge;
          });

          setNodes(flowNodes);
          
          // Update usedAssetIds to include assets that are already nodes in the site config
          const existingNodeIds = new Set(response.data.graph.nodes.map(node => node.id));
          setUsedAssetIds(prev => {
            const newSet = new Set(prev);
            existingNodeIds.forEach(nodeId => newSet.add(nodeId));
            return newSet;
          });
          
          // Small delay to ensure nodes are rendered before edges
          setTimeout(() => {
            setEdges(flowEdges);
          }, 100);
        } else {
          // No graph data available, set empty arrays
          console.log('No graph data available, setting empty nodes and edges');
          setNodes([]);
          setEdges([]);
          // Clear usedAssetIds when no site config data
          setUsedAssetIds(new Set());
        }
        setApiDataLoaded(true);
      } else {
        console.error('Site config API failed:', response.message);
        setSiteConfigError(response.message || 'Failed to fetch site config data');
      }
    } catch (error) {
      console.error('Site config fetch error:', error);
      setSiteConfigError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setSiteConfigLoading(false);
    }
  };

  // Function to fetch assets from API
  const fetchAssets = async () => {
    if (!projectId || !user?.orgId) {
      return;
    }

    setAssetsLoading(true);
    setAssetsError(null);

    try {
      console.log('Fetching project assets for project:', projectId, 'org:', user.orgId);
      const response = await chargersService.getProjectAssets(projectId, user.orgId);
      console.log('Project assets API response:', response);
      
      if (response.success && response.data) {
        console.log('Project assets data received:', response.data);
        setAssets(response.data.assets);
      } else {
        console.error('Project assets API failed:', response.message);
        setAssetsError(response.message || 'Failed to fetch project assets');
      }
    } catch (error) {
      console.error('Project assets fetch error:', error);
      setAssetsError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setAssetsLoading(false);
    }
  };

  // Fetch site config data and assets on component mount
  React.useEffect(() => {
    fetchSiteConfigData();
    fetchAssets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, user?.orgId]);

  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge = {
        ...params,
        type: 'custom'
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges],
  );

  const handleSaveConfiguration = async () => {
    if (!projectId || !user?.orgId) {
      setSnackbar({
        open: true,
        message: 'Project ID or Organization ID not found',
        severity: 'error'
      });
      return;
    }

    try {
      // Transform React Flow nodes and edges to API format
      const apiNodes: SiteConfigNode[] = nodes.map(node => ({
        id: node.id,
        data: {
          label: node.data.label,
          ...(node.data.assetType && { assetType: node.data.assetType }),
        },
        position: {
          x: node.position.x,
          y: node.position.y
        }
      }));

      const apiEdges: SiteConfigEdge[] = edges.map(edge => ({
        id: `[${edge.source}]-[${edge.target}]`,
        source: edge.source,
        target: edge.target,
        ...(edge.sourceHandle && { sourceHandle: edge.sourceHandle })
      }));

      const graph: SiteConfigGraph = {
        nodes: apiNodes,
        edges: apiEdges
      };

      // Make POST request to save site config
      const response = await siteConfigService.saveSiteConfigProject(user.orgId, projectId, graph);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Successfully saved!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to save configuration',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Save configuration error:', error);
      
      let errorMessage = 'Failed to save configuration';
      
      if (error instanceof Error) {
        // Handle specific database errors
        if (error.message.includes('Database operation failed')) {
          errorMessage = 'Database error occurred. Please try again or contact support.';
        } else if (error.message.includes('status: 500')) {
          errorMessage = 'Server error occurred. Please try again later.';
        } else if (error.message.includes('status: 400')) {
          errorMessage = 'Invalid data format. Please check your configuration.';
        } else if (error.message.includes('status: 401')) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (error.message.includes('status: 403')) {
          errorMessage = 'Access denied. You do not have permission to save this configuration.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDeleteConfiguration = async () => {
    if (!projectId || !user?.orgId) {
      setSnackbar({
        open: true,
        message: 'Project ID or Organization ID not found',
        severity: 'error'
      });
      return;
    }

    if (!window.confirm('Delete this site configuration? This cannot be undone.')) {
      return;
    }

    setSiteConfigLoading(true);
    setSiteConfigError(null);

    try {
      const response = await siteConfigService.clearSiteConfigProject(user.orgId, projectId);

      if (response.success) {
        setNodes([]);
        setEdges([]);
        setUsedAssetIds(new Set());
        setSnackbar({
          open: true,
          message: 'Site configuration deleted.',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to delete site configuration',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Delete site configuration error:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to delete site configuration',
        severity: 'error'
      });
    } finally {
      setSiteConfigLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', height: '100vh', display: 'flex', flexDirection: 'column', p: 3 }}>
      {/* Header */}

        <Grid container alignItems="center" justifyContent="space-between" mb="3">
          <Grid item>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Site Configuration
            </Typography>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                sx={{
                  gap: 1, 
                  border: `1px solid ${theme.palette.primary.light}`,
                  '&:hover': {
                    backgroundColor:(theme) => theme.palette.primary.light + '20', 
                    // or any other hover styles you want
                  }
                }} 
                onClick={fetchSiteConfigData} 
                color="primary" 
                disabled={siteConfigLoading}
              >
                  <RefreshIcon sx={{color: theme.palette.primary.light }}/>
                  <Typography variant="h5" component="h1" sx={{color: theme.palette.primary.light }}>
                    RESTORE
                </Typography>
                </Button>
                <Button 
                sx={{
                  gap: 1, 
                  border: `1px solid ${theme.palette.primary.light}`,
                  '&:hover': {
                    backgroundColor:(theme) => theme.palette.primary.light + '20', 
                    // or any other hover styles you want
                  }
                }} 
                onClick={handleSaveConfiguration} 
                //color="primary" 
                disabled={siteConfigLoading}
              >
                  <SaveIcon sx={{color: theme.palette.primary.light }}/>
                  <Typography variant="h5" component="h1" sx={{color: theme.palette.primary.light }}>
                    SAVE
                </Typography>
                </Button>
                <Button 
                sx={{
                  gap: 1, 
                  border: `1px solid ${theme.palette.error.main}`,
                  '&:hover': {
                    backgroundColor:(theme) => theme.palette.error.light + '20', 
                    // or any other hover styles you want
                  }
                }} 
                onClick={handleDeleteConfiguration}
                color="primary" 
                disabled={siteConfigLoading}
              >
                  <ClearIcon sx={{color: theme.palette.error.main }}/>
                  <Typography variant="h5" component="h1" sx={{color: theme.palette.error.main }}>
                    DELETE
                </Typography>
                </Button>
            </Box>
          </Grid>
        </Grid>


      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2}}>
        {/* Sidebar */}
        <Box sx={{ 
          display: 'flex', 

          flexDirection: 'column', 
          gap: 2,  
          
          width: '300px', 
          flexShrink: 0 }}>
          <Typography variant="body2" gutterBottom>
          Drag-and-drop assets from the list to add them to the site configuration diagram. Connect them by dragging lines between the connection points on the boxes.
          </Typography>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              gap: 1,
              pr: 1.5,
              pt: 0.8,
              pb: 0.8,
              pl: 0.5,
              borderRadius: 0.5,
              width: 'fit-content',
              cursor: 'pointer', 
              alignItems: 'center', 
              '&:hover': {
                backgroundColor:(theme) => theme.palette.primary.light + '20', 
              }
            }}
            onClick={() => {
              const newNode: Node = {
                id: `${Date.now()}`,
                type: 'grid',
                position: { x: 0, y: 350 },
                data: { 
                  label: 'Grid',
                  description: 'Power grid connection',
                  color: '#e8f5e8'
                },
              };
              setNodes((nds) => [...nds, newNode]);}}
            >
              <AddIcon sx={{ 
              color: theme.palette.primary.light,
              fontSize: 20
              }} />
              <Typography
                variant="body2"
                sx={{ 
                  fontWeight: 500, 
                  color: theme.palette.primary.light
                }}
                >
                GRID INPUT
              </Typography>
          </Box>

          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              gap: 1,
              pr: 1.5,
              pt: 0.8,
              pb: 0.8,
              pl: 0.5,
              borderRadius: 0.5,
              width: 'fit-content',
              cursor: 'pointer', 
              alignItems: 'center', 
              '&:hover': {
                backgroundColor:(theme) => theme.palette.primary.light + '20', 
              }
            }}
            onClick={() => {
              const newNode: Node = {
                id: `demo-switchboard-${Date.now()}`,
                type: 'custom',
                position: { x: 80, y: 280 },
                data: {
                  label: 'Demo Switchboard',
                  assetType: 'switchboard',
                  description: 'Switchboard',
                  color: '#f5f5f5',
                },
              };
              setNodes((nds) => [...nds, newNode]);
            }}
          >
            <img
              src={process.env.PUBLIC_URL + '/Switchboards.svg'}
              alt="Switchboard"
              style={{ width: 20, height: 20 }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: theme.palette.primary.light
              }}
            >
              SWITCHBOARD
            </Typography>
          </Box>

          {/* Assets List */}
          <Box sx={{ 
            mt: 2,
            
            }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 400,
              color: theme.palette.text.secondary,
              mb: 1
            }}>
              Available assets
            </Typography>
            
            {assetsLoading && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                p: 2,
                }}>
                <CircularProgress size={16} />
                <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                  Loading assets...
                </Typography>
              </Box>
            )}
            
            {assetsError && (
              <Alert severity="error" sx={{ fontSize: '12px', mb: 1 }}>
                {assetsError}
              </Alert>
            )}
            
            {!assetsLoading && !assetsError && assets.filter(asset => !usedAssetIds.has(asset.unit_id) && asset.type !== 'excavator').length === 0 && (
              <Typography variant="body2" sx={{ 
                color: 'rgba(0,0,0,0.6)', 
                p: 2,
                textAlign: 'center'
              }}>
                {assets.length === 0 ? 'No assets available' : 'All assets have been added to the site configuration'}
              </Typography>
            )}
            
            {!assetsLoading && !assetsError && assets.filter(asset => !usedAssetIds.has(asset.unit_id) && asset.type !== 'excavator').length > 0 && (
              <Box sx={{ 
                maxHeight: '400px', 
                overflowY: 'auto',
                //border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1,
                backgroundColor: theme.palette.background.paper,
                cursor: 'pointer'  
              }}>
                {assets.filter(asset => !usedAssetIds.has(asset.unit_id) && asset.type !== 'excavator').map((asset) => (
                  <Box
                    key={asset.unit_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 0.5,
                      //mb: 0.5,
                      borderRadius: 0.5
                    }}
                    onClick={() => {
                      // Add asset to the diagram
                      // Position assets to the right of the Grid node (which is at x: 100, y: 350)
                      const newNode: Node = {
                        id: asset.unit_id,
                        type: 'custom',
                        position: { 
                          x: 160, 
                          y: 350 - 100 + Math.random() * 200   // 100px above to 100px below Grid
                        },
                        data: { 
                          label: asset.unit_name,
                          name: asset.unit_name,
                          assetType: asset.type,
                          description: asset.type || 'Asset',
                          color: '#f5f5f5'
                        },
                      };
                      setNodes((nds) => [...nds, newNode]);
                      
                      // Mark asset as used so it disappears from available list
                      setUsedAssetIds(prev => {
                        const newSet = new Set(prev);
                        newSet.add(asset.unit_id);
                        return newSet;
                      });
                    }}
                  >
                    {asset.type === 'switchboard' ? (
                      <img
                        src={process.env.PUBLIC_URL + '/Switchboards.svg'}
                        alt="Switchboard"
                        style={{ width: 20, height: 20 }}
                      />
                    ) : (
                      <EvStation sx={{ 
                        color: theme.palette.primary.light,
                        fontSize: 20
                      }} />
                    )}
                    <Typography variant="body2" sx={{ 
                      fontWeight: 500,
                      color: theme.palette.primary.light,
                      flex: 1
                    }}>
                      {asset.unit_name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>


        {/* Flow Canvas */}
        <Box sx={{ 
          flex: 1, 
          height: 'calc(100vh - 200px)', 
          position: 'relative',
          backgroundColor: theme.palette.background.default
        }}>
          {siteConfigLoading && (
            <Box sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1
            }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>
                Loading site configuration...
              </Typography>
            </Box>
          )}
          
          {siteConfigError && (
            <Box sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              textAlign: 'center',
              p: 2
            }}>
              <Alert severity="error" sx={{ fontSize: '12px' }}>
                {siteConfigError}
              </Alert>
            </Box>
          )}
          
          {!siteConfigLoading && !siteConfigError && nodes.length === 0 && apiDataLoaded && (
            <Box sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              textAlign: 'center',
              p: 2
            }}>
              <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                No site configuration data available. Use the sidebar to add components.
              </Typography>
            </Box>
          )}
          
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            zoomOnScroll={false}
            attributionPosition="bottom-left"
          >
            <Controls />
          </ReactFlow>
        </Box>
      </Box>


      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SiteConfig;
